import { memo } from 'react';
import { Flex } from '@optiaxiom/react';
import { NodeProps } from '@xyflow/react';

import { useNodeRenderer } from '../../../../hooks/useNodeRenderer';
import { NodeContainer } from '../../container/NodeContainer';
import { NodeBadges } from '../../node-parts/NodeBadges';
import { NodeDescription } from '../../node-parts/NodeDescription';
import { NodeHeader } from '../../node-parts/NodeHeader';
import { nodeContent } from '../../shared/shared.css';

import { baseNodeContainer } from './BaseNodeRenderer.css';

export type BaseNodeRendererProps = {
  data: {
    type: string;
    name?: string;
    description?: string;
    executionStatus?: 'pending' | 'running' | 'completed' | 'failed';
    isSelected?: boolean;
    stepExecution?: Record<string, unknown>;
    executionCount?: number;
    [key: string]: unknown;
  };
} & NodeProps;

export const BaseNodeRenderer = memo(({ data, type, selected, id }: NodeProps) => {
  const dataObj = data as Record<string, unknown>;
  const {
    iconInfo,
    category,
    label,
    description,
    hasErrors,
    isSourceNode,
    handleMouseEnter,
    handleMouseLeave,
    addonLeft,
    addonRight,
    addonBottom,
    nodeTypeName,
    nodeType,
  } = useNodeRenderer({ id, type, data: dataObj, selected });

  // Get execution status for styling
  const executionStatus = dataObj?.executionStatus as
    | 'pending'
    | 'running'
    | 'completed'
    | 'failed'
    | undefined;
  const isExecutionSelected = Boolean(dataObj?.isSelected);

  if (!nodeType) {
    console.warn(`Node type '${nodeTypeName}' not found in registry`);
    return <div>Unknown node type: {nodeTypeName}</div>;
  }

  return (
    <NodeContainer
      selected={selected}
      hasErrors={hasErrors}
      isSourceNode={isSourceNode}
      executionStatus={executionStatus}
      isExecutionSelected={isExecutionSelected}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={baseNodeContainer}
      nodeId={id}
    >
      <Flex className={nodeContent}>
        <NodeHeader iconInfo={iconInfo} label={label} nodeType={nodeTypeName} category={category} />
        <NodeDescription description={description} />
      </Flex>

      <NodeBadges addonLeft={addonLeft} addonRight={addonRight} addonBottom={addonBottom} />
    </NodeContainer>
  );
});

BaseNodeRenderer.displayName = 'BaseNodeRenderer';
